import { getApiBaseUrl } from "./apiBaseUrl";

const API_BASE_URL = getApiBaseUrl();
const KPI_CALC_SYNC_EVENT = "kpi-calculation-sync";
const KPI_CALC_SYNC_STORAGE_KEY = "kpiCalculationSyncState";

type KpiCalculationProgress = {
  companiesDone: number;
  companiesTotal: number;
  kpisDone: number;
  kpisTotal: number;
};

type KpiCalculationFailure = {
  company: string | number | undefined;
  error: string;
};

type KpiCalculationSyncState = {
  calculating: boolean;
  currentCompany: string;
  failures: KpiCalculationFailure[];
  progress: KpiCalculationProgress;
  updatedAt: number;
};

type KpiCompany = {
  id: number | string;
  company_name?: string;
};

type KpiRow = {
  id: number | string;
  sql_equivalent?: string | null;
};

type RequestJsonOptions = RequestInit;

type RunSharedKpiCalculationsArgs = {
  companyIds?: number[] | null;
  kpiIds?: number[] | null;
};

type RunSharedKpiCalculationsResult = {
  companiesTotal: number;
  kpisTotal: number;
  failures: KpiCalculationFailure[];
};

const defaultProgress: KpiCalculationProgress = {
  companiesDone: 0,
  companiesTotal: 0,
  kpisDone: 0,
  kpisTotal: 0,
};

async function fetchWithTimeout(url: string, options: RequestJsonOptions = {}, timeoutMs = 120000): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

async function requestJson<T>(path: string, options: RequestJsonOptions = {}, timeoutMs = 120000): Promise<T> {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const response = await fetchWithTimeout(`${API_BASE_URL}${normalizedPath}`, options, timeoutMs);

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Request failed (${response.status})`);
  }

  return response.json() as Promise<T>;
}

function persistAndDispatch(payload: Omit<KpiCalculationSyncState, "updatedAt">): void {
  const state: KpiCalculationSyncState = {
    ...payload,
    updatedAt: Date.now(),
  };

  try {
    localStorage.setItem(KPI_CALC_SYNC_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage write errors.
  }

  window.dispatchEvent(
    new CustomEvent<KpiCalculationSyncState>(KPI_CALC_SYNC_EVENT, {
      detail: state,
    }),
  );
}

export function readKpiCalculationSyncState(): KpiCalculationSyncState | null {
  try {
    const raw = localStorage.getItem(KPI_CALC_SYNC_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as KpiCalculationSyncState) : null;
  } catch {
    return null;
  }
}

export function subscribeToKpiCalculationSync(callback: (state: KpiCalculationSyncState | null) => void): () => void {
  const onEvent = (event: Event) => {
    const customEvent = event as CustomEvent<KpiCalculationSyncState>;
    callback(customEvent.detail || null);
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== KPI_CALC_SYNC_STORAGE_KEY) {
      return;
    }

    callback(readKpiCalculationSyncState());
  };

  window.addEventListener(KPI_CALC_SYNC_EVENT, onEvent as EventListener);
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener(KPI_CALC_SYNC_EVENT, onEvent as EventListener);
    window.removeEventListener("storage", onStorage);
  };
}

export async function runSharedKpiCalculations({ companyIds = null, kpiIds = null }: RunSharedKpiCalculationsArgs = {}): Promise<RunSharedKpiCalculationsResult> {
  const companiesPayload = await requestJson<{ companies?: KpiCompany[] }>("/api/kpi/companies", { method: "GET" }, 20000);
  const companies = Array.isArray(companiesPayload?.companies) ? companiesPayload.companies : [];

  const companyList = Array.isArray(companyIds) && companyIds.length > 0
    ? companies.filter((company) => companyIds.includes(Number(company.id)))
    : companies;

  let targetKpiIds = Array.isArray(kpiIds)
    ? kpiIds.filter((id) => Number.isFinite(Number(id))).map(Number)
    : null;

  if (!targetKpiIds || targetKpiIds.length === 0) {
    const kpiPayload = await requestJson<{ kpis?: KpiRow[] }>("/api/kpi?include_sql_equivalent=true", { method: "GET" }, 20000);
    const rows = Array.isArray(kpiPayload?.kpis) ? kpiPayload.kpis : [];
    targetKpiIds = rows
      .filter((row) => String(row?.sql_equivalent || "").trim() !== "")
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id));
  }

  const companiesTotal = companyList.length;
  const kpisPerCompany = targetKpiIds.length;
  const kpisTotal = companiesTotal * kpisPerCompany;

  persistAndDispatch({
    calculating: true,
    currentCompany: "",
    failures: [],
    progress: {
      ...defaultProgress,
      companiesTotal,
      kpisTotal,
    },
  });

  if (!companiesTotal || !kpisPerCompany) {
    persistAndDispatch({
      calculating: false,
      currentCompany: "Complete",
      failures: [],
      progress: {
        ...defaultProgress,
        companiesTotal,
        kpisTotal,
      },
    });

    return {
      companiesTotal,
      kpisTotal,
      failures: [],
    };
  }

  const failures: KpiCalculationFailure[] = [];
  let totalProcessed = 0;
  let totalFailed = 0;

  for (let index = 0; index < companyList.length; index += 1) {
    const company = companyList[index];
    const currentCompany = company?.company_name || `Company #${company?.id}`;

    persistAndDispatch({
      calculating: true,
      currentCompany,
      failures,
      progress: {
        companiesDone: index,
        companiesTotal,
        kpisDone: totalProcessed + totalFailed,
        kpisTotal,
      },
    });

    try {
      const result = await requestJson<{ processed?: number; failed?: number }>(
        "/api/kpi/run_calculations",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            company_ids: [Number(company.id)],
            kpi_ids: targetKpiIds,
          }),
        },
        120000,
      );

      totalProcessed += Number(result?.processed || 0);
      totalFailed += Number(result?.failed || 0);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Run calculation failed";
      failures.push({
        company: company?.company_name || company?.id,
        error: message,
      });
      totalFailed += kpisPerCompany;
    }

    persistAndDispatch({
      calculating: true,
      currentCompany,
      failures,
      progress: {
        companiesDone: index + 1,
        companiesTotal,
        kpisDone: totalProcessed + totalFailed,
        kpisTotal,
      },
    });
  }

  persistAndDispatch({
    calculating: false,
    currentCompany: failures.length ? `Done (${failures.length} failed)` : "Complete",
    failures,
    progress: {
      companiesDone: companiesTotal,
      companiesTotal,
      kpisDone: totalProcessed + totalFailed,
      kpisTotal,
    },
  });

  return {
    companiesTotal,
    kpisTotal,
    failures,
  };
}